import React, { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Share2, Check, Sparkles, ArrowLeft } from "lucide-react";

interface TemplateShellProps {
  children: React.ReactNode;
  templateName: string;
  templateSlug: string;
  showTopBar?: boolean;
  showBackButton?: boolean;
}

/**
 * 模板外殼組件
 * 
 * 為所有模板預覽頁面提供統一的平台級配置：
 * 1. 頂部提示列（這是模板預覽 + 分享按鈕 + 立即生成按鈕）
 * 2. 返回主頁按鈕
 * 
 * 使用方式：
 * <TemplateShell templateName="明德中醫診所" templateSlug="tcm-v1">
 *   <Navigation />
 *   <Hero />
 *   ...
 * </TemplateShell>
 */
export default function TemplateShell({
  children,
  templateName,
  templateSlug,
  showTopBar = true,
  showBackButton = true,
}: TemplateShellProps) {
  const [searchParams] = useSearchParams();
  // 帶 full=1 參數才顯示平台 UI（instruction bar、返回按鈕）
  const isFullPreview = searchParams.get("full") === "1";
  const [copied, setCopied] = useState(false);

  // 預設純模板展示（無平台 UI）；帶 full=1 才顯示平台 UI
  const shouldShowTopBar = showTopBar && isFullPreview;
  const shouldShowBackButton = showBackButton && isFullPreview;

  // 分享連結使用無參數短 URL（純模板展示）
  const shareUrl = `${window.location.origin}/pre/${templateSlug}`;

  const handleShare = async () => {
    const shareData = {
      title: `${templateName} - 網站模板`,
      text: `看看這個${templateName}網站模板`,
      url: shareUrl,
    };

    // 優先使用原生 Share API（手機）
    if (navigator.share) {
      try {
        await navigator.share(shareData);
        return;
      } catch (err) {
        // 用戶取消或不支援，fallback 到複製
      }
    }

    // Fallback：複製到剪貼簿
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  const handleGenerate = () => {
    window.location.href = `/start/${templateSlug}`;
  };

  return (
    <>
      {/* 頂部提示列（覆蓋式，不佔空間） */}
      {shouldShowTopBar && (
        <div className="fixed top-0 left-0 right-0 z-[60] bg-gradient-to-r from-pine-800 to-pine-700 text-white shadow-md">
          <div className="container mx-auto px-4 py-3">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
              <div className="flex items-center gap-2 text-sm">
                <Sparkles className="w-4 h-4 shrink-0" />
                <span>
                  這是模板預覽，你可以生成一個屬於自己的網站
                </span>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={handleShare}
                  className="inline-flex items-center gap-1.5 px-4 py-1.5 bg-white/10 hover:bg-white/20 rounded-full text-sm font-medium transition-colors"
                >
                  {copied ? (
                    <>
                      <Check className="w-4 h-4" />
                      已複製連結
                    </>
                  ) : (
                    <>
                      <Share2 className="w-4 h-4" />
                      分享此設計
                    </>
                  )}
                </button>
                <button
                  onClick={handleGenerate}
                  className="inline-flex items-center gap-1.5 px-4 py-1.5 bg-white text-pine-800 hover:bg-rice-50 rounded-full text-sm font-bold transition-colors"
                >
                  <Sparkles className="w-4 h-4" />
                  立即生成
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 返回主頁按鈕（浮動） */}
      {shouldShowBackButton && (
        <Link
          to="/templates"
          className="fixed left-4 z-50 inline-flex items-center gap-2 px-4 py-2 bg-white/90 backdrop-blur-sm hover:bg-white rounded-full shadow-lg text-sm font-medium text-gray-700 hover:text-pine-700 transition-all hover:shadow-xl"
          style={{ top: "5rem" }}
        >
          <ArrowLeft className="w-4 h-4" />
          <span className="hidden sm:inline">返回模板列表</span>
          <span className="sm:hidden">返回</span>
        </Link>
      )}

      {/* 模板內容 */}
      {children}
    </>
  );
}
